import { React, useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateTableActions } from '../../store/update-table-slice';
import axios from 'axios';
import { Delete, Edit, MoreVert } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import Popup from '../Popup';
import EmployeeSalaryForm from '../forms/EmployeeSalaryForm';
import EditSalaryStructureHeader from '../forms/EditSalaryStructureHeader';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../common/constants';

let sal_comp_fetch_id = 0;
let component = 'Amount';
let effectiveDate;
let basic_edit_effective_date = '';



export default function EmployeeMasterTable(props) {

  const [openPopup, setOpenPopup] = useState(false);
  const [openBasicEditPopup, setOpenBasicEditPopup] = useState(false);
  const [openHeaderEditPopup, setOpenHeaderEditPopup] = useState(false);
  const [headerEditData, setHeaderEditData] = useState({ department: '', designation: '', effectiveDate: '' });
  const [openDeleteMenu, setOpenDeleteMenu] = useState(null); // Track which contract's delete menu is open
  const [salComponents, setSalComponents] = useState([]);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
  const [refreshCounter, setRefreshCounter] = useState(0);

  const updateEmpSalComponents = useSelector(state => state.updateTable.updateEmpSalaryComponents);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  

  async function getSalaryComponents() {
    let API_URL = `${process.env.REACT_APP_API_BASE_URL}/employee-master/view-sal-components/` + props.fetch_id;
    if (props.fetch_id === 9999) {
      API_URL = `${process.env.REACT_APP_API_BASE_URL}/employee-master/view-sal-components/` + props.employeeID;
    }
    try {
      const response = await axios.get(API_URL);
      // setSalComponents(response.data.filter(item => item.IsActive === 1));
      setSalComponents(response.data);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    }
  }

  // Refresh function that increments counter
  const triggerRefresh = () => {
    setRefreshCounter(prev => prev + 1);
  };

  useEffect(() => {
    getSalaryComponents();
  }, [refreshCounter]);

  useEffect(() => {
    if (updateEmpSalComponents) {
      getSalaryComponents();
      dispatch(updateTableActions.stopUpdateEmpSalComponents());
    }
  }, [updateEmpSalComponents]);

  // Close delete menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDeleteMenu !== null && !event.target.closest('[data-menu-container]')) {
        setOpenDeleteMenu(null);
      }
    };

    if (openDeleteMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDeleteMenu]);

  // Refresh component on edit.
  // const handleOnEdit = () => {
  //   getSalaryComponents();
  // }

  const handleDelete = async (delete_id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false
    });

    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/employee-master/delete-sal-comp/` + delete_id);
      if (res.status === 200) {
        getSalaryComponents();
        setNotify({
          isOpen: true,
          message: 'Successfully Deleted!',
          type: 'error'
        });
      }
      dispatch(updateTableActions.startUpdateEmpSalComponents());
    } catch (e) {
      if (e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    }
  }

  const handleDeleteBasicSalary = async (employeeId, contractEffectiveDate) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false
    });

    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/employee-master/delete-employee-salary-structure/${employeeId}`;
      const res = await axios.delete(url, { data: { contract_effective_date: contractEffectiveDate } });
      if (res.status === 200 && res.data?.success) {
        getSalaryComponents();
        setNotify({
          isOpen: true,
          message: res.data.message || 'Salary structure deleted successfully',
          type: 'success'
        });
      }
      dispatch(updateTableActions.startUpdateEmpSalComponents());
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.message || 'Error deleting salary structure',
          type: 'error'
        });
      }
    }
  }

  const handleCloseModal = () => {
    setOpenPopup(false);
    // Refresh table data after modal closes
    triggerRefresh();
  }

  const handleCloseBasicEditModal = () => {
    setOpenBasicEditPopup(false);
    triggerRefresh();
  }

  const handleCloseHeaderEditModal = () => {
    setOpenHeaderEditPopup(false);
    triggerRefresh();
  }

  return (
    <div>
      {salComponents.map((contractGroup, groupIndex) => {
        // Remove duplicate components based on employee_contract_id and component name
        const uniqueComponents = contractGroup.components.reduce((acc, current) => {
          const key = `${current.employee_contract_id}_${current.component}`;
          if (!acc.find(item => `${item.employee_contract_id}_${item.component}` === key)) {
            acc.push(current);
          }
          return acc;
        }, []);

        // Get unique employee_contract_ids from the contract group
        const uniqueContractIds = [...new Set(contractGroup.components.map(comp => comp.employee_contract_id))];
        // Use the first contract ID for basic salary deletion
        const contractId = uniqueContractIds.length > 0 ? uniqueContractIds[0] : null;

        return (
          <div key={groupIndex} style={{ marginBottom: '30px' }}>
            {/* Contract Header Card */}
            <div className="table-info" style={{
              borderRadius: '8px',
              padding: '16px 20px',
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #0b418e'
            }}>
              <div style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>Contract Effective Date:</span>
                  <span style={{ fontSize: '16px', fontWeight: '600', marginLeft: '8px' }}>
                    {formatDate(contractGroup.contract_effective_date)}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>CTC:</span>
                  <span style={{ fontSize: '16px', fontWeight: '600', marginLeft: '8px' }}>
                    ₹{contractGroup.ctc?.toLocaleString('en-IN') || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>Basic:</span>
                  <span style={{ fontSize: '16px', fontWeight: '600', marginLeft: '8px' }}>
                    ₹{contractGroup.basic?.toLocaleString('en-IN') || 'N/A'}
                  </span>
                </div>
              </div>
              {contractId && (
                <div style={{ position: 'relative' }} data-menu-container>
                  <Tooltip title="Options" arrow>
                    <button
                      type="button"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: '4px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#666'
                      }}
                      onClick={() => setOpenDeleteMenu(openDeleteMenu === groupIndex ? null : groupIndex)}
                    >
                      <MoreVert />
                    </button>
                  </Tooltip>
                  {openDeleteMenu === groupIndex && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      marginTop: '4px',
                      backgroundColor: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '150px'
                    }} data-menu-container>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 16px',
                          border: 'none',
                          background: 'transparent',
                          color: '#0b418e',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          borderBottom: '1px solid #dee2e6'
                        }}
                        onClick={() => {
                          setOpenDeleteMenu(null);
                          const firstComp = contractGroup.components && contractGroup.components.length > 0 ? contractGroup.components[0] : {};
                          const eff = contractGroup.contract_effective_date && contractGroup.contract_effective_date.includes('T')
                            ? contractGroup.contract_effective_date.split('T')[0]
                            : contractGroup.contract_effective_date;
                          setHeaderEditData({
                            department: firstComp.department || '',
                            designation: firstComp.designation || '',
                            effectiveDate: eff || ''
                          });
                          setOpenHeaderEditPopup(true);
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <Edit style={{ fontSize: '18px' }} />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 16px',
                          border: 'none',
                          background: 'transparent',
                          color: '#dc3545',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px'
                        }}
                        onClick={() => {
                          setOpenDeleteMenu(null);
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Are you sure to delete this salary structure?',
                            subTitle: "You can't undo this operation",
                            onConfirm: () => {
                              const eff = contractGroup.contract_effective_date && contractGroup.contract_effective_date.includes('T')
                                ? contractGroup.contract_effective_date.split('T')[0]
                                : contractGroup.contract_effective_date;
                              const employeeId = props.fetch_id === 9999 ? props.employeeID : props.fetch_id;
                              handleDeleteBasicSalary(employeeId, eff);
                            }
                          });
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <Delete style={{ fontSize: '18px' }} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table for Components */}
            <table class="table">
              <thead class="table-dark">
                <tr>
                  <th scope="col">Department</th>
                  <th scope="col">Designation</th>
                  <th scope="col">Component</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Active</th>
                  <th scope="col" class="table-dark"></th>
                  <th scope="col" class="table-dark"></th>
                </tr>
              </thead>
              <tbody>
                {uniqueComponents.map((salComponent, compIndex) => (
                  <tr key={`${groupIndex}-${compIndex}`}>
                    <td>{salComponent.department}</td>
                    <td>{salComponent.designation}</td>
                    <td>{salComponent.component}</td>
                    <td>{salComponent.Amount?.toLocaleString('en-IN') || salComponent.Amount}</td>
                    <td>{salComponent.IsActive === 1 ? 'Yes' : 'No'}</td>
                    <td>
                      <button 
                        type="button" 
                        class="btn btn-outline-primary btn-sm" 
                        onClick={() => {
                          setOpenPopup(true);
                          sal_comp_fetch_id = salComponent.employee_contract_id;
                          component = salComponent.component;
                          effectiveDate = salComponent.contract_effective_date.includes('T') 
                            ? salComponent.contract_effective_date.split("T")[0]
                            : salComponent.contract_effective_date;
                        }}
                      >
                        <Edit /> 
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        onClick={() => setConfirmDialog({
                          isOpen: true,
                          title: 'Are you sure to delete this record?',
                          subTitle: "You can't undo this operation",
                          onConfirm: () => { handleDelete(salComponent.employee_contract_id) }
                        })}
                      >
                        <Delete />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      <Popup
        title={`Update ${component}`}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <EmployeeSalaryForm title="Modify Employee" salCompFetchID={sal_comp_fetch_id} fetch_id={props.fetch_id} employeeID={props.employeeID} component={component} effectiveDate={effectiveDate} onCloseModal={handleCloseModal} onSuccess={triggerRefresh} disableEffectiveDate={true} />
      </Popup>
      <Popup
        title="Edit Salary Structure"
        openPopup={openHeaderEditPopup}
        setOpenPopup={setOpenHeaderEditPopup}
      >
        <EditSalaryStructureHeader
          employeeID={props.fetch_id === 9999 ? props.employeeID : props.fetch_id}
          initialDepartment={headerEditData.department}
          initialDesignation={headerEditData.designation}
          initialEffectiveDate={headerEditData.effectiveDate}
          disableEffectiveDate={true}
          onClose={handleCloseHeaderEditModal}
          onSuccess={triggerRefresh}
        />
      </Popup>
      <Notification
        notify={notify}
        setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog} />
    </div>
  );
}
